import { Injectable } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { ContainerType } from '../models';
import { ACEPTED_EXTENSION, DocumentationLoadContent, DocumentationType, maxFiles } from '../models/documentation-type.model';
import { FileDocument } from '../models/file-document.model';
import { DocumentationTypesService } from './documentation-types.service';

export interface DocumentationUploadTypeState {
  usingFormio: boolean;
  formName: string;
  isExternalFormOnly: boolean;
  showOptionalFormComplete: boolean;
  rrhhUploadFile: boolean;
  aceptedExtensions: ACEPTED_EXTENSION;
  maxFiles: maxFiles;
}

export interface DocumentationUploadMetadataResult {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  adittionalsMetadatas: any[];
  hasPeriod: boolean;
  documentationDate: Date | null;
}

export interface DocumentationUploadMetadataOptions {
  excludedMetadataSystemName?: string;
}

@Injectable({
  providedIn: 'root'
})
export class DocumentationUploadService {
  constructor(private documentationTypesService: DocumentationTypesService) { }

  getDocumentationTypeState(documentationType: DocumentationType): DocumentationUploadTypeState {
    const showOptionalFormComplete = documentationType.hasExternalForm
      && documentationType.documentationLoadContentId === DocumentationLoadContent.AMBOS;

    return {
      usingFormio: documentationType.exteralForm != null && documentationType.exteralForm.length > 0,
      formName: documentationType.exteralForm,
      isExternalFormOnly: documentationType.isExternalFormOnly,
      showOptionalFormComplete,
      rrhhUploadFile: showOptionalFormComplete
        ? false
        : documentationType.documentationLoadContentName == 'RRHH' || documentationType.documentationLoadContentName == 'AMBOS',
      aceptedExtensions: documentationType.identificationTypeManual ? ACEPTED_EXTENSION.MANUAL : ACEPTED_EXTENSION.AUTOMATICO,
      maxFiles: documentationType.identificationTypeManual ? maxFiles.SIMPLE : maxFiles.MULTIPLE
    };
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getAvailableSequences(documentationType: DocumentationType): any[] {
    return documentationType.documentationTypeSequence.filter(x => x.documentationSequence.workflowId !== '12');
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getDefaultSequence(sequences: any[]): any {
    return sequences.length === 1 ? sequences[0].documentationSequence : null;
  }

  async loadAdditionalMetadatas(
    documentTypeId: number,
    documentation: FileDocument,
    containerType: ContainerType,
    options: DocumentationUploadMetadataOptions = {}
  ): Promise<DocumentationUploadMetadataResult> {
    const data = await firstValueFrom(this.documentationTypesService.getDocumentType(documentTypeId));

    let adittionalsMetadatas = data['metadata'].filter(z =>
      z.metadataSystemName != FileDocument.nroLegSystemName &&
      z.metadataSystemName != FileDocument.nameSystemName &&
      z.metadataSystemName != FileDocument.lastNameSystemName &&
      z.metadataSystemName != FileDocument.cuilSystemName &&
      z.metadataSystemName != FileDocument.dateSystemName &&
      z.metadataSystemName != FileDocument.idDocumentacionSystemName &&
      z.metadataSystemName != FileDocument.nomDocumentacionSystemName &&
      z.metadataSystemName != FileDocument.userIdSystemName &&
      z.metadataId != documentation.documentationTypeSelected.nonConformityReasonId &&
      (containerType.metadata.findIndex(mc => mc.metadataSystemName == z.metadataSystemName) < 0)
    );

    if (documentation.documentationTypeSelected.metadataId != null) {
      const metaAux = adittionalsMetadatas.find(z => z['metadataId'] == documentation.documentationTypeSelected.metadataId);
      if (metaAux != null) {
        documentation.setMetadata(metaAux['metadataSystemName'], documentation.documentationTypeSelected.metadataKey);
        adittionalsMetadatas.splice(adittionalsMetadatas.findIndex(e => e['metadataId'] === documentation.documentationTypeSelected.metadataId), 1);
      }
    }

    if (options.excludedMetadataSystemName) {
      adittionalsMetadatas = adittionalsMetadatas.filter(a => a.metadataSystemName != options.excludedMetadataSystemName);
    }

    const hasPeriod = adittionalsMetadatas.findIndex(m => m.metadataSystemName == FileDocument.periodSystemName) >= 0;

    return {
      adittionalsMetadatas,
      hasPeriod,
      documentationDate: hasPeriod ? new Date() : null
    };
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  addMetadataControls(form: UntypedFormGroup, formBuilder: UntypedFormBuilder, adittionalsMetadatas: any[]): void {
    adittionalsMetadatas.forEach(element => {
      if (form.get(element.metadataSystemName)) {
        return;
      }

      if (element.isRequired) {
        form.addControl(element.metadataSystemName, formBuilder.control('', Validators.required));
      } else {
        form.addControl(element.metadataSystemName, formBuilder.control(''));
      }
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  updateAditionalMetadata(values: any[], event: any, form: UntypedFormGroup): any[] {
    if (!event.systemName) {
      event.systemName = event.metadataSystemName;
    }

    event.metadataSystemName = event.systemName;

    const nextValues = [...values];
    const index = nextValues.findIndex((e) => e.metadataSystemName === event.metadataSystemName);
    if (index === -1) {
      nextValues.push(event);
    } else {
      nextValues[index] = event;
    }

    form.get(event.metadataSystemName)?.setValue(event.metadataValue);
    return nextValues;
  }

  hasFiles(documentation: FileDocument, files: File[]): boolean {
    if (documentation.formioFormAlias && documentation.formioSubmissionId) {
      return documentation.formioFormAlias.length > 0 && documentation.formioSubmissionId.length > 0;
    }

    return (files != null && files.length > 0) || documentation.documentationTypeSelected.documentationLoadContentName != 'RRHH';
  }

  getMaxFiles(documentation: FileDocument): maxFiles {
    return documentation.documentationTypeSelected.identificationTypeManual ? maxFiles.SIMPLE : maxFiles.MULTIPLE;
  }

  isFilePreviewable(files: File[]): boolean {
    const extension = files[0].name.split('.').pop()?.toLowerCase();
    return extension !== 'tif' && extension !== 'tiff' && extension !== 'zip';
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getEmployeeNotifyValue(sequence: any, employeeNotify: boolean): boolean {
    return !!(sequence && sequence.id != 1 && employeeNotify);
  }
}
